import React, {useEffect, useState} from 'react';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {useUnmountCancelToken} from '../../../srcCommon/helpers/hookHelpers';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {ApiRuleBundle, RuleBundleModel} from '../../models/api/ruleBundleModel';
import {fetchApiAsync} from '../../services/http/http';
import {getCompany} from '../../store/data/company/companySelectors';
import {CustomFields} from '../editors/commons/customFieldsModel';
import {CsatEditorWrapper} from './ruleEditor/recipes/csat/csatEditorWrapper';
import {RequiredTaggingEditor} from './ruleEditor/recipes/requiredTagging/requiredTaggingEditor';
import {SlaEditorWrapper} from './ruleEditor/recipes/sla/slaEditorWrapper';
import {RuleEditorSkeleton} from './ruleEditor/ruleEditorSkeleton';
import {parseApiError} from './ruleHelpers';
import {RuleIntegration} from './ruleIntegrationModel';
import {RuleComplexRecipeTypesEnum} from './ruleRecipeModel';

/*
 * Props.
 */

interface SettingsRulesComplexRecipesEditorProps {
  recipe: RuleComplexRecipeTypesEnum;
  bundleId?: number;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  isEditingRule: boolean;
  namespace: string;
  ruleIntegration: RuleIntegration;
  customFields: CustomFields;

  navigateToRules: Handler;
  setErrorBannerMessage: HandlerOf<any>;

  // Feature flags.
  hasHighlightErrorsFlag: boolean;
}

/*
 * Component.
 */

export const SettingsRulesComplexRecipesEditor: React.FC<SettingsRulesComplexRecipesEditorProps> = (
  props,
) => {
  const {
    recipe,
    breadcrumbs,
    bundleId,
    isEditingRule,
    namespace,
    customFields,
    ruleIntegration,

    setErrorBannerMessage,
    navigateToRules,

    hasHighlightErrorsFlag,
  } = props;

  const company = useAppSelector(getCompany);

  // Rule bundle.
  const [ruleBundle, setRuleBundle] = useState<RuleBundleModel>();

  const cancelToken = useUnmountCancelToken();

  useEffect(() => {
    if (bundleId !== undefined) {
      fetchApiAsync(ApiRuleBundle, `${company.url}/rule_bundles/${bundleId}/rules`, {
        cancelToken,
      }).then(
        (r) => setRuleBundle(r),
        (err) => setErrorBannerMessage(parseApiError(err)),
      );
    }
  }, [bundleId, cancelToken, company.url, namespace, recipe, setErrorBannerMessage]);

  if (bundleId !== undefined && ruleBundle === undefined) {
    return (
      <RuleEditorSkeleton
        breadcrumbs={breadcrumbs}
        namespace={namespace}
        isEditing={isEditingRule}
        isActive
      />
    );
  }

  switch (recipe) {
    case RuleComplexRecipeTypesEnum.REQUIRED_TAGGING:
      return (
        <RequiredTaggingEditor
          namespace={namespace}
          ruleIntegration={ruleIntegration}
          customFields={customFields}
          breadcrumbs={breadcrumbs}
          navigateToRules={navigateToRules}
          ruleBundle={ruleBundle}
          hasHighlightErrorsFlag={hasHighlightErrorsFlag}
        />
      );

    case RuleComplexRecipeTypesEnum.SLA:
      return (
        <SlaEditorWrapper
          namespace={namespace}
          ruleIntegration={ruleIntegration}
          customFields={customFields}
          breadcrumbs={breadcrumbs}
          navigateToRules={navigateToRules}
          ruleBundle={ruleBundle}
          isEditingRule={isEditingRule}
        />
      );

    case RuleComplexRecipeTypesEnum.CSAT:
      return (
        <CsatEditorWrapper
          namespace={namespace}
          ruleIntegration={ruleIntegration}
          customFields={customFields}
          breadcrumbs={breadcrumbs}
          navigateToRules={navigateToRules}
          ruleBundle={ruleBundle}
          isEditingRule={isEditingRule}
        />
      );

    default:
      // Should never throw.
      throw new UnreachableError(recipe);
  }
};
